import { useParams } from 'next/navigation';
import { useEsiteCtx } from 'services/esite/context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocationsQuery } from 'services/locations';
import { getQueryParams } from '../utils/get-locations-query-params';
import { getFilteredStoreLocations } from '../utils/filter-store-locations';
import { PCC_OPERATIONAL_COUNTRY_FIRST_FEATURE_FLAG } from 'constants/featureFlag';
import { useFeatureFlagQuery } from 'services/featureFlag/useFeatureFlagQuery';

export interface Coordinates {
  lat: number;
  lng: number;
}
export type LocationsSearch = Coordinates | string;
const useLocations = (restrictedDealerStores = []) => {
  const { storeId, locale } = useEsiteCtx();
  const params = useParams();
  const [searchValue, setSearchValue] = useState<LocationsSearch>('');
  const [isIdle, setIsIdle] = useState(true);
  const [showStoresOutsideOfCountry, setShowStoresOutsideOfCountry] =
    useState(false);
  const queryParams = getQueryParams({
    searchValue,
    showStoresOutsideOfCountry,
    storeId,
    locale
  });

  const context = {
    kind: 'location',
    key: params.esite,
    esite: params.esite
  };

  const { data: flag = {} } = useFeatureFlagQuery(
    PCC_OPERATIONAL_COUNTRY_FIRST_FEATURE_FLAG,
    context
  );
  const isShowToggleForExtendStoreList =
    flag?.[PCC_OPERATIONAL_COUNTRY_FIRST_FEATURE_FLAG] ?? false;

  const { data, isPending, isFetching, isError, refetch } =
    useLocationsQuery(queryParams);
  const filteredData = useMemo(
    () => getFilteredStoreLocations(restrictedDealerStores, data, searchValue),
    [restrictedDealerStores, data, searchValue]
  );
  const hideResults = isIdle || isPending || !filteredData?.locations?.length;

  const getLocations = useCallback((value: LocationsSearch) => {
    setSearchValue(value);
    setIsIdle(false);
    setShowStoresOutsideOfCountry(false);
  }, []);

  const resetIsIdle = useCallback(() => {
    setIsIdle(true);
  }, []);

  const setExtendMapToggle = useMemo(() => {
    return (toggle: boolean) => setShowStoresOutsideOfCountry(toggle);
  }, []);

  useEffect(() => {
    if (searchValue && showStoresOutsideOfCountry) {
      refetch();
    }

    if (searchValue && !showStoresOutsideOfCountry) {
      refetch();
    }
  }, [showStoresOutsideOfCountry, searchValue, refetch]);

  const showInCountryToggle =
    isShowToggleForExtendStoreList || filteredData?.showInCountryToggle;

  return {
    getLocations: getLocations,
    hideResults,
    isLoading: isIdle ? false : isPending,
    searchValue: isIdle ? '' : isPending,
    isIdle,
    isError,
    isFetching: showInCountryToggle && isFetching,
    ...(isIdle ? {} : filteredData),
    reset: resetIsIdle,
    showInCountryToggle,
    isExtendMapToggleChecked: showInCountryToggle && showStoresOutsideOfCountry,
    setIsExtendMapToggleChecked: setExtendMapToggle
  };
};

export default useLocations;
